const present = value => value !== undefined && value !== null && value !== ''
const keyOf = value => String(value)
const userIdOf = row => present(row?.organization_id) ? undefined : row?.user_id ?? row?.id ?? row?.value

export const filterWorkMemberOptions = (items = [], query = '') => {
  const keyword = String(query || '').trim().toLocaleLowerCase()
  if (!keyword) return items
  return items.filter(item => [
    item?.name,
    item?.text,
    item?.email,
    item?.organization_root_name,
    item?.organization_sub_name,
  ].some(value => String(value || '').toLocaleLowerCase().includes(keyword)))
}

export const reconcileWatcherIds = (assigneeId, watcherIds = []) => {
  const assigneeKey = present(assigneeId) ? keyOf(assigneeId) : ''
  const seen = new Set()
  return watcherIds.filter(id => {
    if (!present(id)) return false
    const key = keyOf(id)
    if (key === assigneeKey || seen.has(key)) return false
    seen.add(key)
    return true
  })
}

export const toggleWatcherId = (watcherIds = [], userId) => {
  if (!present(userId)) return reconcileWatcherIds('', watcherIds)
  const key = keyOf(userId)
  const current = reconcileWatcherIds('', watcherIds)
  return current.some(id => keyOf(id) === key)
    ? current.filter(id => keyOf(id) !== key)
    : [...current, userId]
}

export const splitWorkItemAssignments = (assignees = [], fallbackAssignee = null) => {
  const rows = Array.isArray(assignees) ? assignees.filter(Boolean) : []
  const assignee = rows.find(row => present(userIdOf(row)) && (row.role === 'assignee' || !row.role)) || fallbackAssignee || null
  const watchers = rows.filter(row => row.role === 'watcher')
  const assigneeId = userIdOf(assignee)
  return {
    assignee,
    assigneeId: present(assigneeId) ? assigneeId : '',
    watcherIds: reconcileWatcherIds('', watchers.map(userIdOf)),
    watchers,
  }
}

export const buildWorkItemAssignments = (assigneeId, watcherIds = []) => [
  ...(present(assigneeId) ? [{ user_id: assigneeId, role: 'assignee', notify_enabled: true }] : []),
  ...reconcileWatcherIds(assigneeId, watcherIds).map(userId => ({
    user_id: userId,
    role: 'watcher',
    notify_enabled: true,
  })),
]

// Only assignment source fields belong in a write payload; never expand organizations here.
export const normalizeAssignmentSources = (rows = []) => (Array.isArray(rows) ? rows : [])
  .filter(row => row && (present(row.organization_id) || present(userIdOf(row))))
  .map(row => ({
    ...(present(row.organization_id)
      ? { organization_id: row.organization_id, include_descendants: row.include_descendants === true }
      : { user_id: userIdOf(row) }),
    role: row.role || 'assignee',
    notify_enabled: row.notify_enabled !== false,
  }))

export const assignmentSourcesForEdit = (item = {}) => {
  // An explicit empty array is authoritative, especially for completed work.
  if (Array.isArray(item.assignee_sources)) return normalizeAssignmentSources(item.assignee_sources)
  if (Array.isArray(item.assignees)) return normalizeAssignmentSources(item.assignees)
  return buildWorkItemAssignments(item.default_assignee_id || userIdOf(item.assignee), item.watcher_ids || [])
}

export const buildChangedWorkItemAssignments = (item = {}, changes = {}) => {
  if (Object.prototype.hasOwnProperty.call(changes, 'sources')) return normalizeAssignmentSources(changes.sources)
  let rows = assignmentSourcesForEdit(item)
  if (Object.prototype.hasOwnProperty.call(changes, 'assigneeId')) {
    const previous = rows.find(row => String(row.user_id) === String(changes.assigneeId) && row.role === 'assignee')
    rows = rows.filter(row => row.organization_id || row.role !== 'assignee')
    if (present(changes.assigneeId)) rows.unshift(previous || { user_id: changes.assigneeId, role: 'assignee', notify_enabled: true })
  }
  if (Object.prototype.hasOwnProperty.call(changes, 'watcherIds')) {
    const watchers = reconcileWatcherIds('', changes.watcherIds || []).map(id =>
      rows.find(row => String(row.user_id) === String(id) && row.role === 'watcher')
      || { user_id: id, role: 'watcher', notify_enabled: true })
    rows = [...rows.filter(row => row.role !== 'watcher'), ...watchers]
  }
  return rows
}

export const assignmentSourcePatch = sources => {
  const rows = normalizeAssignmentSources(sources)
  const state = splitWorkItemAssignments(rows)
  return { assignee_sources: rows, assignees: rows, default_assignee_id: state.assigneeId, watcher_ids: state.watcherIds }
}

export const changeWorkItemAssignee = (item = {}, assigneeId) => ({
  ...item,
  ...assignmentSourcePatch(buildChangedWorkItemAssignments(item, { assigneeId })),
})

export const assignmentSourceLabel = (row, members = [], organizations = []) => {
  const organization = present(row?.organization_id)
  const id = organization ? row.organization_id : userIdOf(row)
  const option = (organization ? organizations : members).find(value => String(value.value ?? value.id) === String(id))
  const name = option?.text || option?.name || row?.name || row?.display_name || row?.username
    || `${organization ? '조직' : '사용자'} #${id}`
  return organization ? `${name}${row.include_descendants ? ' (하위 조직 포함)' : ''}` : name
}

export const assignmentDisplayText = (item = {}, members = [], organizations = []) => {
  // Actual work always displays effective users (including the completion snapshot).
  const rows = Array.isArray(item.assignees) ? item.assignees
    : item.assignee ? [item.assignee] : assignmentSourcesForEdit(item)
  return rows.filter(row => !row.role || row.role === 'assignee')
    .map(row => assignmentSourceLabel(row, item.status === 'completed' ? [] : members, organizations)).join(', ') || '담당자 미지정'
}

export const assignmentSourceDisplayRows = (item = {}, members = [], organizations = []) => {
  const hasSources = Array.isArray(item.assignee_sources)
  const rows = hasSources ? item.assignee_sources
    : Array.isArray(item.assignees) ? item.assignees
      : item.assignee ? [item.assignee] : assignmentSourcesForEdit(item)
  const directory = !hasSources && item.status === 'completed' ? [] : members
  return rows.filter(row => row && (!row.role || row.role === 'assignee')).map(row => {
    const organization = present(row.organization_id)
    const id = organization ? row.organization_id : userIdOf(row)
    const candidate = (organization ? organizations : directory).find(option => String(option.value ?? option.id) === String(id))
    const effectiveUser = !organization && present(id)
      ? (item.assignees || []).find(user => String(userIdOf(user)) === String(id) && (!user.role || user.role === 'assignee'))
      : null
    const display = { ...(candidate || effectiveUser || {}), ...row }
    return { ...display, text: assignmentSourceLabel(display, directory, organizations) }
  })
}

export const assignmentSourceDisplayText = (item = {}, members = [], organizations = []) =>
  assignmentSourceDisplayRows(item, members, organizations).map(row => row.text).join(', ') || '담당자 미지정'

export const changeRuntimeAssignment = (item = {}, strategy) => {
  const next = { ...item }
  if (strategy === 'sources') {
    next.assignees = Array.isArray(item.assignees) ? normalizeAssignmentSources(item.assignees)
      : (item.assignment?.user_ids || []).map(user_id => ({ user_id, role: 'assignee', notify_enabled: true }))
    delete next.assignment
  } else if (strategy === 'system_auto') {
    delete next.assignment
    if (Array.isArray(next.assignees)) next.assignees = normalizeAssignmentSources(next.assignees).filter(row => row.role !== 'assignee')
  } else if (strategy === 'conditions') {
    delete next.assignees
    next.assignment = { strategy: 'conditions', on_unresolved: 'fail', conditions: [] }
  } else {
    delete next.assignees
    next.assignment = strategy === 'fixed_user'
      ? { strategy, user_ids: [], on_unresolved: 'fail' }
      : { strategy: 'value_resolver', value_from: { type: 'iteration_value' }, resolver: { type: 'project_user', match_field: 'user_name', match_operator: 'equals' }, on_unresolved: 'fail' }
  }
  return next
}
